/**
 * CapaAgingBar — horizontal stacked bar showing the 4 age buckets of
 * open CAPAs. Pure SVG (no chart lib). The 90+ bucket is rendered red,
 * 60–90 amber, 30–60 yellow-amber, <30 green. Each segment carries an
 * aria-label so screen readers get the breakdown.
 */

export interface CapaAgingBarProps {
  total: number;
  buckets: { lt30: number; b30to60: number; b60to90: number; gt90: number };
  width?: number;
  height?: number;
}

const BUCKET_COLOURS = {
  lt30:    'var(--color-status-success, #15803D)',
  b30to60: 'var(--color-status-warning, #B45309)',
  b60to90: '#D97706',
  gt90:    'var(--color-status-error, #B91C1C)',
};
const BUCKET_LABELS = {
  lt30:    '0–30 days',
  b30to60: '31–60 days',
  b60to90: '61–90 days',
  gt90:    '>90 days',
};

export function CapaAgingBar({ total, buckets, width = 720, height = 36 }: CapaAgingBarProps) {
  if (total === 0) {
    return (
      <div style={{ width, color: 'var(--color-text-tertiary, #6B7989)', fontSize: 13, padding: 12, textAlign: 'center' }}>
        No open CAPAs.
      </div>
    );
  }
  let cursor = 0;
  const segments: { key: string; x: number; w: number; v: number; colour: string; label: string }[] = [];
  (['lt30', 'b30to60', 'b60to90', 'gt90'] as const).forEach((k) => {
    const v = buckets[k];
    const w = (v / total) * width;
    segments.push({ key: k, x: cursor, w, v, colour: BUCKET_COLOURS[k], label: BUCKET_LABELS[k] });
    cursor += w;
  });
  return (
    <div>
      <svg width={width} height={height} role="img" aria-label={`CAPA aging: ${segments.map((s) => `${s.v} in ${s.label}`).join(', ')}`}>
        {segments.map((s) => (
          s.w > 0 ? (
            <g key={s.key}>
              <rect x={s.x} y={0} width={s.w} height={height} fill={s.colour} />
              {s.w > 30 && (
                <text x={s.x + s.w / 2} y={height / 2 + 4} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={600}>
                  {s.v}
                </text>
              )}
            </g>
          ) : null
        ))}
      </svg>
      <ol style={{ display: 'flex', gap: 16, padding: 0, margin: '8px 0 0', listStyle: 'none', flexWrap: 'wrap' }}>
        {segments.map((s) => (
          <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary, #4B5563)' }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: s.colour }} />
            <span>{s.label}</span>
            <strong style={{ color: 'var(--color-text-primary, #111827)' }}>{s.v}</strong>
          </li>
        ))}
      </ol>
    </div>
  );
}
